import { computed, watch } from "vue";

import { redirectWithTracking } from "@solvari/argus/thin-client";
import { extendUrlParams, parseUrl, toSameOriginPath } from "@solvari/utils";

import type { ClientFeature } from "@/client/features.ts";

import { whenAbTestingLoaded } from "@/client/loaded.ts";
import { getAbTestFeature } from "@/client/reader.ts";
import { getSentryAbTesting } from "@/client/sentry.ts";
import { resolveSplitUrlOutcome } from "@/features/splitUrl/core.ts";
import {
  splitUrlFeatureKey,
  splitUrlMapFeatureKey,
} from "@/registry/featureRegistry.ts";

// Structural type → apps pass useRouter() without depending on vue-router.
type AbTestingRouter = {
  isReady: () => Promise<void>;
  replace: (to: string) => Promise<unknown>;
  resolve: (to: string) => { matched: unknown[] };
};

type SplitUrlRedirect = (url: string, clientSide?: boolean) => void;

function createSplitUrlRedirect(
  router?: AbTestingRouter,
  redirect = redirectWithTracking,
): SplitUrlRedirect {
  if (!router) {
    return (url) => redirect(url);
  }

  return (url, clientSide = false) => {
    const path = clientSide ? toSameOriginPath(url) : null;

    if (path === null || router.resolve(path).matched.length === 0) {
      redirect(url);
      return;
    }

    void router
      .isReady()
      .then(() => router.replace(path))
      .catch(() => redirect(url));
  };
}

async function fireDeferredTrackingCalls(): Promise<void> {
  const abTesting = globalThis.abTesting;
  if (abTesting && "fireDeferredTrackingCalls" in abTesting) {
    await abTesting.fireDeferredTrackingCalls();
  }
}

function splitUrlFeature({
  redirect,
  router,
}: {
  redirect?: SplitUrlRedirect;
  router?: AbTestingRouter;
} = {}): ClientFeature {
  return async (instance, { navigationReady }) => {
    await whenAbTestingLoaded();
    // A decision taken before navigation settles would redirect off the wrong path.
    await navigationReady;

    const followTarget = redirect ?? createSplitUrlRedirect(router);
    const splitUrl = getAbTestFeature<string>(splitUrlFeatureKey);
    const splitUrlMap = getAbTestFeature<Record<string, string>>(
      splitUrlMapFeatureKey,
    );

    // `urlFull`: the reactive attribute GrowthBook targets on → one url source, one decision.
    // Defaults to "" before the first attribute update → fall back on empty too.
    const currentUrl = computed(() => {
      const raw: unknown = instance.growthBook.getAttributes().urlFull;

      return typeof raw !== "string" || raw === "" ? window.location.href : raw;
    });

    const outcome = computed(() =>
      resolveSplitUrlOutcome({
        currentUrl: currentUrl.value,
        splitUrl: splitUrl.value,
        splitUrlMap: splitUrlMap.value,
      }),
    );

    watch(
      outcome,
      async (outcome) => {
        if (outcome.status === "not-in-test") {
          getSentryAbTesting().captureException(
            new Error(
              `Tried to run split url test but the currentPage was not included in the test: ${parseUrl(currentUrl.value)?.pathname ?? currentUrl.value}`,
            ),
          );
          return;
        }

        if (outcome.status === "unnavigable") {
          getSentryAbTesting().captureException(
            new Error(
              `Split url test names a target that cannot be followed: ${outcome.target}`,
            ),
          );
          return;
        }

        if (outcome.status === "none") {
          return;
        }

        await fireDeferredTrackingCalls();
        followTarget(extendUrlParams(outcome.url), outcome.clientSide);
      },
      { deep: true, immediate: true },
    );
  };
}

export type { AbTestingRouter, SplitUrlRedirect };
export { createSplitUrlRedirect, splitUrlFeature };
